<template>
	<div class="bg-primary-25 w-full p-6 print:py-3">
		<h2
			class="form-title pb-6 text-2xl font-bold print:mb-3 print:border-b-2 print:border-slate-900 print:pt-2 print:pb-3 print:text-sm/0"
		>
			{{ t('plan.sectionTitle.intermentPreferences') }}
		</h2>

		<CopyButtonList
			@click="copyIntermentPreferences"
			:active-index="activePlanIndex"
			class="print:hidden"
		/>

		<section class="print:*:break-inside-avoid">
			<div
				class="section-grid-row grid grid-cols-1 gap-2 *:w-full *:max-w-none"
			>
				<div class="flex pb-6">
					<CardRadio
						v-model="burialPreference"
						name="burialOrCremation"
						:options="burialOrCremationOptions"
						@update:model-value="activePlan.intermentPrefs = ''"
					/>
				</div>
			</div>
			<div
				class="section-grid-row grid grid-cols-3 gap-2 *:w-full *:max-w-none"
			>
				<FormKit
					type="dropdown"
					name="cemeteryPurchaseStatus"
					:label="t('plan.cemeteryPurchaseStatus')"
					:placeholder="
						t('plan.placeholders.select', {
							placeholderLabel: t('plan.cemeteryPurchaseStatus'),
						})
					"
					:options="cemeteryPurchaseStatusOptions"
				/>

				<FormKit
					type="dropdown"
					:label="t('plan.intermentPreference')"
					:placeholder="
						t('plan.placeholders.select', {
							placeholderLabel: t('plan.intermentPreference'),
						})
					"
					name="intermentPrefs"
					:options="intermentPref"
				/>

				<FormKit
					type="textarea"
					auto-height
					outer-class="w-full max-w-none"
					name="cemeteryPropertyOwner"
					:label="t('plan.cemeteryPropertyOwner')"
				/>
			</div>
			<div
				class="section-grid-row grid grid-cols-3 gap-2 *:w-full *:max-w-none"
			>
				<FormKit
					type="textarea"
					auto-height
					outer-class="w-full max-w-none"
					name="cemeteryName"
					:label="t('plan.cemeteryName')"
				/>

				<FormKit
					type="textarea"
					auto-height
					name="intermentCity"
					:label="t('person.city')"
				/>

				<FormKit
					type="autocomplete"
					name="intermentState"
					:label="t('person.state')"
					:placeholder="
						t('plan.placeholders.select', {
							placeholderLabel: t('person.state'),
						})
					"
					:options="stateOptions"
					clear-search-on-open
				/>
			</div>
			<div
				class="section-grid-row grid grid-cols-1 gap-2 *:w-full *:max-w-none"
				:class="{ 'print:hidden': !activePlan?.intermentNotes?.trim() }"
			>
				<FormKit
					type="textarea"
					auto-height
					name="intermentNotes"
					:label="t('plan.notes')"
					:outer-class="{ 'mb-0': true }"
					:placeholder="t('plan.placeholders.intermentNotes')"
					validation="length:0,10000"
					validation-visibility="live"
				/>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import BurialCard from '@/components/memorialGuide/BurialCard.vue'
import CardRadio from '@/components/memorialGuide/CardRadio.vue'
import CremationCard from '@/components/memorialGuide/CremationCard.vue'
import CopyButtonList from '@/components/presentation/CopyButtonList.vue'
import { stateOptions } from '@/data/stateOptions'
import { CemeteryPurchaseStatus } from '@/enums/CemeteryPurchaseStatus'
import { Disposition } from '@/enums/Disposition'
import { BurialInterment, CremationInterment } from '@/enums/Interment'
import { usePresentationLocale } from '@/i18n'
import { usePlanStore } from '@/stores/plan'
import { FormKit } from '@formkit/vue'
import { computed, nextTick } from 'vue'

const { t } = usePresentationLocale()

const props = defineProps<{
	activePlanIndex: number
	activePlan: Plan
}>()

/**
 * The two options for burial or cremation radio cards.
 */
const burialOrCremationOptions = [
	{
		label: t('plan.disposition.burial'),
		value: Disposition.Burial,
		icon: BurialCard,
	},
	{
		label: t('plan.disposition.cremation'),
		value: Disposition.Cremation,
		icon: CremationCard,
	},
]

/**
 * The options for Cemetery Purchase Status.
 */
const cemeteryPurchaseStatusOptions = [
	{
		label: t('plan.cemeteryPurchaseStatusOptions.purchased'),
		value: CemeteryPurchaseStatus.Purchased,
	},
	{
		label: t('plan.cemeteryPurchaseStatusOptions.notPurchased'),
		value: CemeteryPurchaseStatus.NotPurchased,
	},
	{
		label: t('plan.cemeteryPurchaseStatusOptions.notInterested'),
		value: CemeteryPurchaseStatus.NotInterested,
	},
]

/** Interment preference options for burial */
const intermentBurialOptions = Object.values(BurialInterment).map((option) => ({
	label: t(`plan.burialInterment.${option}`),
	value: option,
}))

/** Interment preference options for cremation */
const intermentCremationOptions = Object.values(CremationInterment).map(
	(option) => ({
		label: t(`plan.cremationInterment.${option}`),
		value: option,
	})
)

/**
 * Returns a list of interment preferences based on the active plan's burial or cremation preference.
 * If there is no active plan return an empty array.
 */
const intermentPref = computed<
	{ label: string; value: BurialInterment | CremationInterment }[]
>(() => {
	if (props.activePlan?.burialOrCremation === Disposition.Burial) {
		return intermentBurialOptions
	} else if (props.activePlan?.burialOrCremation === Disposition.Cremation) {
		return intermentCremationOptions
	}

	return []
})

/** Copy interment preferences from one plan to another */
async function copyIntermentPreferences(sourcePlanIndex: number) {
	const planStore = usePlanStore()
	const sourcePlan = planStore.plans[sourcePlanIndex]

	if (!sourcePlan) throw new Error('Source plan not found')

	const fields: Array<keyof Plan> = [
		'cemeteryPurchaseStatus',
		'intermentPrefs',
		'cemeteryPropertyOwner',
		'cemeteryName',
		'intermentCity',
		'intermentState',
		'intermentNotes',
	]

	// First we copy burialOrCremation so the interment preference options can be recomputed
	planStore.copyProperties(sourcePlan, props.activePlan, 'burialOrCremation')
	await nextTick()
	planStore.copyProperties(sourcePlan, props.activePlan, fields)
}

const burialPreference = computed({
	get: () => String(props.activePlan?.burialOrCremation || ''),
	set: (value) => {
		if (props.activePlan) {
			props.activePlan.burialOrCremation = value as Disposition
		}
	},
})
</script>
